<template>
	<v-dialog v-model="show" max-width="600" persistent>
		<v-card>
			<v-card-title>
				<span class="headline">Publishing Template</span>
			</v-card-title>
			<v-card-text>
				<div class="mb-4">
					<p>Publishing template content to {{ websites.length }} website{{ websites.length !== 1 ? 's' : '' }}:</p>
				</div>

				<div class="progress-container">
					<div v-for="(website, index) in websites" :key="website._id" class="website-progress-item mb-3">
						<div class="d-flex align-items-center">
							<div class="website-info flex-grow-1">
								<div class="font-weight-medium">{{ website.name }}</div>
								<div class="text-caption text--secondary">{{ website.domain }}</div>
							</div>
							<div class="status-icon ml-3">
								<app-icon v-if="getWebsiteStatus(index) === 'pending'"
									name="fa-clock"
									color="grey"
									size="20"
								/>
								<app-icon v-else-if="getWebsiteStatus(index) === 'publishing'"
									name="fa-spinner fa-spin"
									color="primary"
									size="20"
								/>
								<app-icon v-else-if="getWebsiteStatus(index) === 'success'"
									name="fa-check-circle"
									color="success"
									size="20"
								/>
								<app-icon v-else-if="getWebsiteStatus(index) === 'error'"
									name="fa-exclamation-circle"
									color="error"
									size="20"
								/>
							</div>
						</div>
						<div v-if="websiteErrors[website._id]" class="error-message mt-2">
							<div class="text-caption error--text">
								{{ websiteErrors[website._id] }}
							</div>
						</div>
					</div>
				</div>

				<div class="mt-4">
					<v-progress-linear
						:value="progressPercentage"
						height="8"
						color="primary"
						class="mb-2"
					></v-progress-linear>
					<div class="text-center text-caption">
						{{ currentIndex }} of {{ websites.length }} completed
						<span v-if="!isComplete"> - {{ (currentWebsite && currentWebsite.name) || 'Starting...' }}</span>
					</div>
				</div>

				<div v-if="isComplete" class="mt-4 text-center">
					<div v-if="hasErrors" class="error--text">
						<app-icon name="fa-exclamation-triangle" class="mr-1" />
						Publishing completed with {{ errorCount }} error{{ errorCount !== 1 ? 's' : '' }}
					</div>
					<div v-else class="success--text">
						<app-icon name="fa-check-circle" class="mr-1" />
						All websites published successfully!
					</div>
				</div>
			</v-card-text>
			<v-card-actions>
				<v-spacer></v-spacer>
				<app-button
					v-if="!isComplete"
					color="error"
					text
					@click="onCancel"
					:disabled="isPublishing"
				>
					Cancel
				</app-button>
				<app-button
					v-if="isComplete"
					color="primary"
					@click="onClose"
				>
					Close
				</app-button>
			</v-card-actions>
		</v-card>
	</v-dialog>
</template>

<script>
export default {
	props: {
		value: {
			type: Boolean,
			default: false,
		},
		templateId: {
			type: String,
			required: true,
		},
	},
	data() {
		return {
			websites: [],
			currentIndex: 0,
			isPublishing: false,
			isComplete: false,
			isCancelled: false,
			websiteErrors: {},
		}
	},
	computed: {
		show: {
			get() {
				return this.value
			},
			set(value) {
				this.$emit('input', value)
			},
		},
		progressPercentage() {
			if (this.websites.length === 0) return 0
			return Math.round((this.currentIndex / this.websites.length) * 100)
		},
		currentWebsite() {
			return this.websites[this.currentIndex] || null
		},
		hasErrors() {
			return Object.keys(this.websiteErrors).length > 0
		},
		errorCount() {
			return Object.keys(this.websiteErrors).length
		},
	},
	watch: {
		value(newValue) {
			if (newValue) {
				this.startPublishing()
			} else {
				this.reset()
			}
		},
	},
	methods: {
		async startPublishing() {
			this.reset()
			this.isPublishing = true

			try {
				// Fetch all websites that use this template
				await this.fetchWebsitesUsingTemplate()

				if (this.websites.length === 0) {
					this.$notifications.pushAlert('info', 'No websites found using this template')
					this.onClose()
					return
				}

				// Start sequential publishing
				await this.publishSequentially()
			} catch (error) {
				this.$notifications.pushAlert('error', error.message || 'Failed to start publishing process')
				this.onClose()
			}
		},

		async fetchWebsitesUsingTemplate() {
			let allWebsites = []
			let offset = 0
			const limit = 100 // Set a reasonable limit per request
			let hasMore = true

			while (hasMore) {
				const request = this.$ravello.graphql('websites')
					.filter('template', this.templateId)
					.fields(['_id', 'name', 'domain', 'slug'])
					.limit(limit)
					.offset(offset)

				// eslint-disable-next-line no-await-in-loop
				const websites = await request.query()

				if (!websites || websites.length === 0) {
					hasMore = false // No more results
				} else {
					allWebsites = allWebsites.concat(websites)

					// If we got fewer results than the limit, we've reached the end
					if (websites.length < limit) {
						hasMore = false
					} else {
						offset += limit
					}
				}
			}

			this.websites = allWebsites
		},

		async publishSequentially() {
			for (let i = 0; i < this.websites.length && !this.isCancelled; i++) {
				this.currentIndex = i
				const website = this.websites[i]

				try {
					// eslint-disable-next-line no-await-in-loop
					await this.publishWebsiteStrings(website._id)
				} catch (error) {
					this.websiteErrors[website._id] = error.message || 'Publishing failed'
				}
			}

			if (!this.isCancelled) {
				this.currentIndex = this.websites.length
				this.isComplete = true
			}

			this.isPublishing = false
		},

		async publishWebsiteStrings(websiteId) {
			await this.$ravello.request()
				.v(2)
				.to('websites')
				.to(websiteId)
				.to('strings')
				.to('build')
				.create()
		},

		getWebsiteStatus(index) {
			const website = this.websites[index]

			if (this.websiteErrors[website._id]) {
				return 'error'
			}

			if (index < this.currentIndex) {
				return 'success'
			}

			if (index === this.currentIndex && this.isPublishing) {
				return 'publishing'
			}

			return 'pending'
		},

		onCancel() {
			this.isCancelled = true
			this.isPublishing = false
			this.onClose()
		},

		onClose() {
			this.show = false
			this.$emit('complete', {
				published: this.currentIndex,
				errors: this.errorCount,
				websites: this.websites,
			})
		},

		reset() {
			this.websites = []
			this.currentIndex = 0
			this.isPublishing = false
			this.isComplete = false
			this.isCancelled = false
			this.websiteErrors = {}
		},
	},
}
</script>

<style scoped>
.progress-container {
	max-height: 300px;
	overflow-y: auto;
}

.website-progress-item {
	padding: 12px;
	border: 1px solid #e0e0e0;
	border-radius: 4px;
	background-color: #fafafa;
}

.website-info {
	min-width: 0;
}

.error-message {
	padding: 8px;
	background-color: #ffebee;
	border-radius: 4px;
	border-left: 4px solid #f44336;
}

.status-icon {
	min-width: 32px;
	text-align: center;
}
</style>
